import { EHR } from '@getvim/vim-os-sdk-api';
import equal from 'fast-deep-equal';

export const notifyEhrStateChangeSymbol = Symbol('notifyEhrStateChangeSymbol');
export const notifyOrderCreatedSymbol = Symbol('notifyOrderCreatedSymbol');
export class WorkflowEventsApi<EHR_STATE extends EHR.EHR_STATE_TEMPLATE>
  implements EHR.WorkflowEvents<EHR_STATE>
{
  #televisitSubscribers: Set<EHR.WorkflowCallback<EHR_STATE>> = new Set();
  #previousEhrState: EHR_STATE | undefined;
  #orderCreatedSubscribers: Set<(order: EHR.Order) => void> = new Set();

  // A way to only expose the notify function internally
  [notifyEhrStateChangeSymbol](previousEhrState: EHR_STATE, currentEhrState: EHR_STATE) {
    this.#previousEhrState = previousEhrState;
    const oldTelevisit = previousEhrState?.encounter?.televisit;
    const newTelevisit = currentEhrState?.encounter?.televisit;
    if (!equal(oldTelevisit, newTelevisit)) {
      for (const cb of this.#televisitSubscribers) {
        try {
          cb({ prevState: previousEhrState, newState: currentEhrState });
        } catch {
          // Secure error handling: do not leak details
        }
      }
    }
  }

  public encounter = {
    /**
     * Subscribe to changes in the televisit state under encounter.
     * Callback receives (oldTelevisit, newTelevisit) with correct types.
     *
     * @param callback (oldTelevisit, newTelevisit) => void
     * @returns unsubscribe function
     */
    onTelevisitChanged: (callback: EHR.WorkflowCallback<EHR_STATE>) => {
      this.#televisitSubscribers.add(callback);
      if (this.#previousEhrState) {
        callback({ prevState: this.#previousEhrState, newState: this.#previousEhrState });
      }
      return () => this.#televisitSubscribers.delete(callback);
    },
  };

  public order = {
    onOrderCreated: (callback: (order: EHR.Order) => void) => {
      this.#orderCreatedSubscribers.add(callback);
      return () => this.#orderCreatedSubscribers.delete(callback);
    },
  };
  [notifyOrderCreatedSymbol](order: EHR.Order) {
    for (const cb of this.#orderCreatedSubscribers) {
      try {
        cb(order);
      } catch {
        // swallow to avoid breaking others
      }
    }
  }
}
